<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ณ วันที่</label>
          <date-picker
            v-model="asOfDate"
            type="date"
            format="DD MMM YY"
            :clearable="false"
            :disabled-date="notAfterToday"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>
        <div class="field">
          <label class="field-lbl">ผู้จ่าย</label>
          <div class="chip-group" role="group" aria-label="ผู้จ่าย">
            <button
              v-for="c in payerChips"
              :key="c.value"
              type="button"
              class="chip"
              :class="{ 'chip-active': payerFilter === c.value }"
              :aria-pressed="payerFilter === c.value"
              @click="payerFilter = c.value"
            >
              {{ c.text }}
            </button>
          </div>
        </div>
        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">แสดงข้อมูล</b-button>
      </div>
    </div>

    <div class="toolbar">
      <div class="aging-legend" aria-label="ช่วงอายุหนี้">
        <span v-for="b in buckets" :key="b.key"><i class="aging-sw" :class="b.key"></i>{{ b.label }}</span>
      </div>
      <div class="toolbar-right">
        <b-button size="sm" variant="outline-success" aria-label="ส่งออก Excel" title="ส่งออก Excel" :disabled="!report" @click="excelExport">
          <i class="fas fa-file-download"></i>
        </b-button>
      </div>
    </div>

    <div class="table-responsive">
      <table id="agingSummaryTable" class="table b-table table-sm aging-table">
        <thead class="thead-light">
          <tr>
            <th>ผู้จ่าย</th>
            <th>สัดส่วน</th>
            <th v-for="b in buckets" :key="b.key" class="text-right aging-age-col">{{ b.label }}</th>
            <th class="text-right aging-age-col">รวมค้างรับ</th>
            <th class="text-right aging-in-col">รับเงินแล้ว {{ monthLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="buckets.length + 4" class="text-center text-muted py-3">ไม่มีข้อมูลให้แสดง</td>
          </tr>
          <tr v-for="r in rows" :key="r.payer" :class="{ 'aging-sel': selected.payer === r.payer }">
            <td>
              <button type="button" class="aging-payer" :aria-expanded="selected.payer === r.payer" @click="select(r.payer, 'all')">
                <i class="fas" :class="selected.payer === r.payer ? 'fa-caret-down' : 'fa-caret-right'"></i>
                {{ r.label }}
              </button>
              <small v-if="r.payer === 'NHSO'" class="d-block text-muted">700 บาท/visit</small>
            </td>
            <td>
              <div class="aging-bar" role="img" :aria-label="r.barLabel" :style="{ width: `${r.barWidth}px` }">
                <span v-for="seg in r.segments" :key="seg.key" :class="seg.key" :style="{ width: `${seg.pct}%` }"></span>
              </div>
            </td>
            <td v-for="b in buckets" :key="b.key" class="text-right" :class="{ 'aging-late': b.late && r.buckets[b.key].count }">
              <button
                v-if="r.buckets[b.key].count"
                type="button"
                class="aging-cell"
                :class="{ on: selected.payer === r.payer && selected.bucket === b.key }"
                @click="select(r.payer, b.key)"
              >
                {{ formatAmount(r.buckets[b.key].amount) }}
                <small class="d-block text-muted">{{ r.buckets[b.key].count }} {{ r.unit }}</small>
              </button>
              <span v-else class="text-muted">0.00</span>
            </td>
            <td class="text-right">
              <button type="button" class="aging-cell" :class="{ on: selected.payer === r.payer && selected.bucket === 'all' }" @click="select(r.payer, 'all')">
                <b>{{ formatAmount(r.total.amount) }}</b>
                <small class="d-block text-muted">{{ r.total.count }} {{ r.unit }}</small>
              </button>
            </td>
            <td class="text-right">
              {{ formatAmount(r.receivedThisMonth.amount) }}
              <small class="d-block text-muted">{{ r.receivedThisMonth.statements }} เอกสาร</small>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="rows.length > 1">
          <tr class="aging-foot">
            <td colspan="2">รวม</td>
            <td v-for="b in buckets" :key="b.key" class="text-right">{{ formatAmount(sumOf((r) => r.buckets[b.key].amount)) }}</td>
            <td class="text-right">{{ formatAmount(sumOf((r) => r.total.amount)) }}</td>
            <td class="text-right">{{ formatAmount(sumOf((r) => r.receivedThisMonth.amount)) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- drill-down: the claims behind the picked payer / bucket -->
    <section v-if="selectedRow" class="aging-drill" aria-labelledby="aging-drill-title">
      <div class="aging-drill-head">
        <h6 id="aging-drill-title" class="mb-0 font-weight-bold">{{ drillTitle }}</h6>
        <div class="seg" role="group" aria-label="ช่วงอายุหนี้">
          <button
            v-for="b in drillBuckets"
            :key="b.key"
            type="button"
            class="seg-btn"
            :class="{ active: selected.bucket === b.key }"
            :aria-pressed="selected.bucket === b.key"
            @click="selected.bucket = b.key"
          >
            {{ b.label }} · {{ b.count }}
          </button>
        </div>
        <span class="flex-grow-1"></span>
        <b-button size="sm" variant="outline-primary" :to="reconcileLink">
          เปิดในหน้ากระทบยอด <i class="fas fa-external-link-alt ml-1"></i>
        </b-button>
      </div>
      <b-table
        id="agingDrillTable"
        small
        hover
        sort-icon-left
        head-variant="light"
        :items="drillClaims"
        :fields="drillFields"
        responsive
        show-empty
        empty-text="ไม่มีรายการในช่วงนี้"
        class="mb-0"
      >
        <template #cell(serviceDate)="row">{{ formatDate(row.value) }}</template>
        <template #cell(amount)="row">{{ formatAmount(row.value) }}</template>
        <template #cell(age)="row">
          <span class="aging-age" :class="row.item.bucket">{{ row.value }} วัน</span>
        </template>
      </b-table>
    </section>

    <p class="small text-muted mt-2 mb-0">
      กดชื่อผู้จ่ายหรือตัวเลขในตารางเพื่อดูรายการ · อายุหนี้นับจากวันที่ใช้สิทธิถึงวันที่เลือก ·
      นับเฉพาะใบเสร็จที่ไม่ถูกยกเลิก · สปสช. นับ 1 visit = 700 บาท
      <template v-if="report && report.startDate">
        · นับเฉพาะวันที่ใช้สิทธิตั้งแต่ {{ formatDate(report.startDate) }} (วันที่เริ่มใช้ระบบกระทบยอด)
      </template>
    </p>
    <Dialog ref="Dialog" />
    <Loading v-if="isLoading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { exportTableToExcel } from "@/utils/reportExport";
import { apiErrorMessage, formatThaiShortDate, bucketServiceRange } from "@/utils/reconciliation";

const BUCKETS = [
  { key: "d0_30", label: "0–30 วัน" },
  { key: "d31_60", label: "31–60 วัน" },
  { key: "d61_90", label: "61–90 วัน", late: true },
  { key: "d90p", label: "เกิน 90 วัน", late: true },
];
const PAYER_LABEL = { SSO: "ประกันสังคม", NHSO: "สปสช." };
const BAR_MAX = 220;

/**
 * ลูกหนี้ สปสช./ปกส.: what SSO / NHSO still owed at the end of a day, aged into buckets, with a
 * drill-down to the claims and a link to the reconciliation page filtered to the same claims.
 */
export default {
  name: "ReconciliationAgingReport",
  components: { Dialog, Loading },
  data() {
    return {
      asOfDate: new Date(),
      payerFilter: "ALL",
      report: null,
      isLoading: false,
      selected: { payer: null, bucket: "all" },
      buckets: BUCKETS,
      payerChips: [
        { value: "ALL", text: "ทั้งหมด" },
        { value: "SSO", text: "ประกันสังคม" },
        { value: "NHSO", text: "สปสช." },
      ],
    };
  },
  computed: {
    ...mapGetters({ branchInfo: "moduleBranch/getBranchInfo", clinicInfo: "moduleBranch/getClinicInfo" }),
    asOf() {
      return moment(this.asOfDate).format("YYYY-MM-DD");
    },
    monthLabel() {
      return this.report ? formatThaiShortDate(this.report.asOf).replace(/^\d+ /, "") : "";
    },
    rows() {
      if (!this.report) return [];
      const payers = ["SSO", "NHSO"].filter((p) => this.payerFilter === "ALL" || this.payerFilter === p);
      const maxTotal = Math.max(1, ...payers.map((p) => this.report.payers[p].total.amount));
      return payers.map((payer) => {
        const d = this.report.payers[payer];
        const total = d.total.amount;
        return {
          payer,
          label: PAYER_LABEL[payer],
          unit: payer === "NHSO" ? "visit" : "รายการ",
          ...d,
          barWidth: Math.max(4, Math.round((total / maxTotal) * BAR_MAX)),
          segments: BUCKETS.map((b) => ({ key: b.key, pct: total ? (d.buckets[b.key].amount / total) * 100 : 0 })),
          barLabel: BUCKETS.map((b) => `${b.label} ${this.formatAmount(d.buckets[b.key].amount)}`).join(", "),
        };
      });
    },
    selectedRow() {
      return this.rows.find((r) => r.payer === this.selected.payer) || null;
    },
    drillBuckets() {
      if (!this.selectedRow) return [];
      return [
        { key: "all", label: "ทั้งหมด", count: this.selectedRow.total.count },
        ...BUCKETS.map((b) => ({ key: b.key, label: b.label.replace(" วัน", ""), count: this.selectedRow.buckets[b.key].count })),
      ];
    },
    drillClaims() {
      if (!this.selectedRow) return [];
      const claims = this.selectedRow.claims.filter((c) => this.selected.bucket === "all" || c.bucket === this.selected.bucket);
      return [...claims].sort((a, b) => b.age - a.age);
    },
    drillTitle() {
      const r = this.selectedRow;
      const b = BUCKETS.find((x) => x.key === this.selected.bucket);
      const amount = this.drillClaims.reduce((sum, c) => sum + Number(c.amount || 0), 0);
      return `${r.label} · ${b ? `ค้าง ${b.label}` : "ค้างรับทั้งหมด"} · ${this.drillClaims.length} ${r.unit} · ฿${this.formatAmount(amount)}`;
    },
    drillFields() {
      const nhso = this.selected.payer === "NHSO";
      return [
        { key: "serviceDate", label: "วันที่ใช้สิทธิ", sortable: true },
        { key: "cid", label: "เลขบัตร ปชช.", sortable: true },
        { key: "patientName", label: "ชื่อ-สกุล", sortable: true },
        ...(nhso ? [ { key: "doctorName", label: "แพทย์", sortable: true } ] : []),
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", sortable: true },
        { key: "amount", label: nhso ? "ยอดเบิก" : "จำนวนเงิน", sortable: true, tdClass: "text-right" },
        { key: "age", label: "ค้างมา", sortable: true, tdClass: "text-right" },
      ];
    },
    // opens the reconciliation page on the payer's tab, ค้างรับ, filtered to the same service dates
    reconcileLink() {
      const range = bucketServiceRange(this.selected.bucket, this.asOf);
      return {
        name: "Reconciliation",
        params: { clinicUrl: this.$route.params.clinicUrl, branchUrl: this.$route.params.branchUrl },
        query: { payer: this.selected.payer, ...range },
      };
    },
  },
  mounted() {
    this.fetchReport();
  },
  methods: {
    ...mapActions("moduleReconciliation", ["fetchAging"]),
    async fetchReport() {
      this.isLoading = true;
      try {
        this.report = await this.fetchAging({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          asOf: this.asOf,
        });
        if (this.selected.payer && !this.rows.some((r) => r.payer === this.selected.payer)) {
          this.selected = { payer: null, bucket: "all" };
        }
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "โหลดรายงานลูกหนี้ไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.isLoading = false;
      }
    },
    select(payer, bucket) {
      this.selected = { payer, bucket };
    },
    sumOf(pick) {
      return this.rows.reduce((sum, r) => sum + Number(pick(r) || 0), 0);
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    excelExport() {
      const sheets = [{ name: "สรุป", tableId: "agingSummaryTable" }];
      if (this.selectedRow) sheets.push({ name: "รายการ", tableId: "agingDrillTable", reportTitle: this.drillTitle });
      exportTableToExcel({
        reportTitle: `ลูกหนี้ สปสช./ปกส. ณ ${formatThaiShortDate(this.asOf)}`,
        reportNameEn: "Payer_Aging",
        dateFrom: this.asOf,
        dateTo: this.asOf,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets,
      });
    },
    formatDate(date) {
      return formatThaiShortDate(date);
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.aging-legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 11.5px;
  color: #4a5568;
}
.aging-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.aging-sw {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.d0_30 { background: #9cc0fa; }
.d31_60 { background: #2a6df4; }
.d61_90 { background: #f0a73a; }
.d90p { background: #a8520a; }
.aging-table th.aging-age-col { background: #fdf1dc; }
.aging-table th.aging-in-col { background: #e3f4e8; }
.aging-table tr.aging-sel td { background: #f3f7ff; }
.aging-table .aging-late { color: #8a4108; font-weight: 700; }
.aging-foot td { background: #dfe3e8; font-weight: 700; }
.aging-bar {
  display: flex;
  height: 12px;
  border-radius: 3px;
  overflow: hidden;
  background: #f1f3f6;
}
.aging-bar span { display: block; height: 100%; }
.aging-payer {
  background: transparent;
  border: 0;
  padding: 0;
  font-weight: 700;
  color: #1f2933;
  min-height: 32px;
}
.aging-payer .fas { color: #0069d9; width: 10px; }
.aging-cell {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 2px 6px;
  margin: -3px -7px;
  color: #0062cc;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-align: right;
}
.aging-cell small { text-decoration: none; }
.aging-cell.on {
  border-color: #2a6df4;
  background: #eaf1fe;
  text-decoration: none;
  font-weight: 700;
}
.aging-drill {
  border: 1px solid #c9dafc;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 12px;
}
.aging-drill-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: #f3f7ff;
  border-bottom: 1px solid #c9dafc;
}
.aging-age {
  display: inline-block;
  min-width: 52px;
  text-align: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eef1f4;
  color: #4a5568;
  font-weight: 600;
}
.aging-age.d61_90 { background: #fff4e0; color: #8a5300; }
.aging-age.d90p { background: #fbe4d3; color: #7a3a06; }
</style>
